import React, { FC } from 'react'
import { Image } from '@/atoms/Image'
import { Modal } from '@/molecules/Modal'
import { formatPrice } from '@/services/PriceFormatter'
import { useTranslate } from '@/utils/translate/translate-client'
import { BonusGiftType } from '@/views/PreSales/hooks/useBonusGift'

const formatBonusDate = (date: Date) => {
  const month = date.toLocaleString('en-US', { month: 'short' })
  const day = date.getDate()
  const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 || Math.floor((day % 100) / 10) === 1 ? 0 : day % 10]

  return `${month}. ${day}${suffix}`
}

interface ShareProps {
  isOpen: boolean
  onClose: () => void
  bonusGift: BonusGiftType
  onCtaClick: () => void
}

export const BonusGift: FC<ShareProps> = ({ isOpen, onClose, bonusGift, onCtaClick }) => {
  const { t } = useTranslate('tickets')
  const { imageUrl, valueInPennies, lastDateToEnter, winnerAnnouncedDate } = bonusGift
  const prizeValue = formatPrice(valueInPennies, { currency: 'USD', includeDecimals: false })
  const enterByDate = formatBonusDate(lastDateToEnter)
  const winnerDate = formatBonusDate(winnerAnnouncedDate)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="mx-4"
      childWrapperClassName="!m-0 !p-0"
      panelClasses="!w-full !bg-homestead-tan-200 !px-4 !py-8 md:!p-10 !max-w-[369px] md:!max-w-[600px]"
    >
      <div className="absolute inset-0 bg-[url(https://images.angelstudios.com/image/upload/v1729801080/dirt-texture-on-white-01.webp)] bg-cover bg-no-repeat opacity-[.16] mix-blend-multiply" />
      <div className="relative z-10 grid justify-items-center gap-[25px] text-center">
        <div className="grid gap-1">
          <p className="!font-[dharma-gothic-e] text-[2rem] font-black uppercase leading-[2rem] tracking-[0.5px] text-homestead-primary-blue md:leading-[36px]">
            {t('enterYourChanceToWinAV3', 'Watch Homestead in Theaters and you Could Win a')}
          </p>
          <h2 className="!font-[dharma-gothic-e] text-[2.75rem] font-bold uppercase leading-[44px] tracking-[1px] text-homestead-red-dark md:text-[3.5rem] md:leading-[56px] md:tracking-[1.25px]">
            {t('threeThousandHomestead', '$300k Homestead')}
          </h2>
          <p className="!font-[bio-sans] text-[19px] font-semibold uppercase leading-[19px] text-homestead-blue md:text-[24px] md:leading-[24px]">
            {t('enterByDateForChanceToWinV2', 'Plus enter by {{enterByDate}} for a Chance at our Bonus Prize!', {
              enterByDate,
            })}
          </p>
          <p className='"!font-[bio-sans] md:text-2xl" text-sm font-medium leading-[21px] text-homestead-primary-blue'>
            {t(
              'bonusAnnounceDateV4',
              'Bonus prize valued at {{prizeValue}}, and winner will be announced the week of {{winnerDate}}.',
              { winnerDate, prizeValue },
            )}
          </p>
        </div>
        <Image
          className="md:!h-[255px] md:!w-[483px]"
          priority
          src={imageUrl}
          alt="bonus gift"
          width={329}
          height={173}
        />
        <button
          onClick={onCtaClick}
          className="homestead-btn-sm rounded-xl bg-homestead-red px-6 py-3.5 uppercase text-white md:mt-2"
        >
          {t('bonusGiftPreOrder', 'Pre-order tickets now')}
        </button>
      </div>
    </Modal>
  )
}
